import { useState } from "react";

import { ProvenanceActComposer } from "./ProvenanceActComposer.tsx";
import { ProvenanceVerificationState } from "./ProvenanceVerificationState.tsx";
import { PROVENANCE_LABELS } from "./provenance.constants.ts";
import type {
  VerificationAction,
  VerificationIntent,
  VerificationModel,
  VerificationPanel,
} from "./provenance.model.ts";

/**
 * How an entry offers its verification, whichever way its surface offers it.
 *
 * The one place the two shapes meet. Neither branch knows which screen it is
 * on — `panelFor` decided that from the surface config before this ran.
 */
export function ProvenanceVerificationPanel({
  panel,
  onAct,
  onUnact,
}: {
  panel: VerificationPanel;
  onAct: (intent: VerificationIntent) => string | null;
  onUnact: (actId: string) => void;
}) {
  if (panel.mode === "act") {
    return <ProvenanceVerification model={panel.acts} onAct={onAct} onUnact={onUnact} />;
  }

  return (
    <>
      <ProvenanceVerificationState state={panel.state} />
      {/* Null when nothing this surface offers is open to this reader. What a
          closed control would have said is already a sentence above. */}
      {panel.acts !== null && (
        <ProvenanceVerification model={panel.acts} onAct={onAct} onUnact={onUnact} />
      )}
    </>
  );
}

/**
 * Verify, dispute, comment, ask.
 *
 * On a composing surface every button renders whatever its state, and one that
 * is closed carries the reason — hidden would be tidier and would leave a
 * collector guessing why an action they were shown in a mock is not there. A
 * reading surface hands this only the ones that are open, because there the
 * reasons are stated above rather than hung on a disabled control.
 *
 * The only state here is which composer is open. Which acts are offered, whether
 * one is already taken and what a refusal says are all the model's.
 */
export function ProvenanceVerification({
  model,
  onAct,
  onUnact,
}: {
  model: VerificationModel;
  onAct: (intent: VerificationIntent) => string | null;
  onUnact: (actId: string) => void;
}) {
  const [composing, setComposing] = useState<VerificationAction["key"] | null>(null);
  const open = model.actions.find((action) => action.key === composing);

  return (
    <section className="provenance__subcard">
      <h4 className="provenance__subcard-title">{PROVENANCE_LABELS.verification}</h4>

      {model.note !== null && <p className="provenance__help">{model.note}</p>}

      <div className="provenance__actions">
        {model.actions.map((action) => (
          <button
            key={action.key}
            type="button"
            className={`provenance__action provenance__action--${action.key}`}
            disabled={!action.available}
            title={action.unavailable ?? action.taken ?? undefined}
            aria-pressed={action.pressed ?? undefined}
            onClick={() => {
              if (action.compose) setComposing(action.key);
              else onAct({ key: action.key } as VerificationIntent);
            }}
          >
            {action.label}
          </button>
        ))}
      </div>

      {model.actions
        .filter((action) => action.taken !== null)
        .map((action) => (
          <p key={action.key} className="provenance__taken">
            {action.taken}
          </p>
        ))}

      {open?.compose && (
        <ProvenanceActComposer
          composer={open.compose}
          onRecord={(value) => onAct(toIntent(open.key, value))}
          onCancel={() => setComposing(null)}
        />
      )}

      {model.queued.length > 0 && (
        <ul className="provenance__queued">
          {model.queued.map((act) => (
            <li key={act.id} className="provenance__queued-item">
              <span className="provenance__queued-label">{act.label}</span>
              <span className="provenance__queued-detail">{act.detail}</span>
              <button
                type="button"
                className="provenance__doc-remove"
                aria-label={act.removeLabel}
                onClick={() => onUnact(act.id)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** So the view never learns that a request names a party and a comment carries a body. */
function toIntent(key: VerificationAction["key"], value: string): VerificationIntent {
  if (key === "request") return { key, party: value };
  if (key === "verify") return { key };
  return { key, body: value };
}
